import React, { useCallback, useEffect, useRef, useState } from 'react';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import './userPanelCategoryCarousel.css';

const SCROLL_EDGE_TOLERANCE = 6;
const SCROLL_ANIMATION_MIN_DURATION = 460;
const SCROLL_ANIMATION_MAX_DURATION = 680;

const easeOutQuart = (progress) => 1 - ((1 - progress) ** 4);

function UserPanelCategoryCarousel({
  id,
  className,
  ariaLabel,
  previousLabel = 'Kategorileri sola kaydır',
  nextLabel = 'Kategorileri sağa kaydır',
  children,
}) {
  const viewportRef = useRef(null);
  const scrollAnimationFrameRef = useRef(null);
  const [scrollState, setScrollState] = useState({ canPrevious: false, canNext: false });

  const updateScrollState = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    const maxScrollLeft = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    const nextState = {
      canPrevious: viewport.scrollLeft > SCROLL_EDGE_TOLERANCE,
      canNext: maxScrollLeft > SCROLL_EDGE_TOLERANCE
        && viewport.scrollLeft < maxScrollLeft - SCROLL_EDGE_TOLERANCE,
    };

    setScrollState((current) => (
      current.canPrevious === nextState.canPrevious && current.canNext === nextState.canNext
        ? current
        : nextState
    ));
  }, []);

  const stopScrollAnimation = useCallback(() => {
    if (scrollAnimationFrameRef.current !== null) {
      window.cancelAnimationFrame(scrollAnimationFrameRef.current);
      scrollAnimationFrameRef.current = null;
    }

    viewportRef.current?.classList.remove('is-soft-scrolling');
  }, []);

  const animateScrollTo = useCallback((requestedLeft) => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    stopScrollAnimation();

    const maxScrollLeft = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    const targetLeft = Math.min(maxScrollLeft, Math.max(0, requestedLeft));
    const startLeft = viewport.scrollLeft;
    const distance = targetLeft - startLeft;

    if (Math.abs(distance) <= 1) {
      viewport.scrollLeft = targetLeft;
      updateScrollState();
      return;
    }

    const duration = Math.min(
      SCROLL_ANIMATION_MAX_DURATION,
      Math.max(SCROLL_ANIMATION_MIN_DURATION, Math.abs(distance) * 0.42)
    );
    let startedAt = null;

    viewport.classList.add('is-soft-scrolling');

    const tick = (timestamp) => {
      if (startedAt === null) startedAt = timestamp;

      const progress = Math.min(1, (timestamp - startedAt) / duration);
      viewport.scrollLeft = startLeft + (distance * easeOutQuart(progress));

      if (progress < 1) {
        scrollAnimationFrameRef.current = window.requestAnimationFrame(tick);
        return;
      }

      viewport.scrollLeft = targetLeft;
      viewport.classList.remove('is-soft-scrolling');
      scrollAnimationFrameRef.current = null;
      updateScrollState();
    };

    scrollAnimationFrameRef.current = window.requestAnimationFrame(tick);
  }, [stopScrollAnimation, updateScrollState]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    viewport.scrollLeft = 0;
    updateScrollState();
  }, [updateScrollState]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return undefined;

    const frameId = window.requestAnimationFrame(updateScrollState);
    const resizeObserver = typeof ResizeObserver === 'function'
      ? new ResizeObserver(updateScrollState)
      : null;

    resizeObserver?.observe(viewport);
    window.addEventListener('resize', updateScrollState);

    return () => {
      window.cancelAnimationFrame(frameId);
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateScrollState);
    };
  }, [children, updateScrollState]);

  useEffect(() => () => stopScrollAnimation(), [stopScrollAnimation]);

  const scrollCategories = (direction) => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    const firstCard = viewport.querySelector(':scope > [role="tab"]');
    const columnGap = Number.parseFloat(window.getComputedStyle(viewport).columnGap) || 10;
    const cardWidth = firstCard?.offsetWidth
      || firstCard?.getBoundingClientRect().width
      || Math.max(142, viewport.clientWidth / 7);
    const visibleCardCount = Math.max(1, Math.floor((viewport.clientWidth + columnGap) / (cardWidth + columnGap)));
    const cardStride = cardWidth + columnGap;
    const pageStep = Math.max(1, visibleCardCount - 1);
    const currentCardIndex = Math.round(viewport.scrollLeft / cardStride);
    const targetCardIndex = Math.max(0, currentCardIndex + (direction * pageStep));

    animateScrollTo(targetCardIndex * cardStride);
  };

  return (
    <div className="tvup-category-carousel">
      <button
        type="button"
        className="tvup-category-carousel-nav is-previous"
        aria-label={previousLabel}
        aria-controls={id}
        disabled={!scrollState.canPrevious}
        onClick={() => scrollCategories(-1)}
      >
        <ChevronLeftRoundedIcon aria-hidden="true" />
      </button>

      <div
        id={id}
        ref={viewportRef}
        className={className}
        role="tablist"
        aria-label={ariaLabel}
        onScroll={updateScrollState}
        onPointerDown={stopScrollAnimation}
        onTouchStart={stopScrollAnimation}
        onWheel={stopScrollAnimation}
      >
        {children}
      </div>

      <button
        type="button"
        className="tvup-category-carousel-nav is-next"
        aria-label={nextLabel}
        aria-controls={id}
        disabled={!scrollState.canNext}
        onClick={() => scrollCategories(1)}
      >
        <ChevronRightRoundedIcon aria-hidden="true" />
      </button>
    </div>
  );
}

export default UserPanelCategoryCarousel;
